import { ReactNode, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { FaInstagram, FaFacebook, FaYoutube, FaWhatsapp } from 'react-icons/fa';
import { ROUTE_PATHS, WHATSAPP_NUMBER, CONTACT_EMAIL, CONTACT_PHONE } from '@/lib/index';
import { Button } from '@/components/ui/button';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { to: ROUTE_PATHS.HOME, label: 'Home' },
    { to: ROUTE_PATHS.PROPERTIES, label: 'Properties' },
    { to: ROUTE_PATHS.INVESTMENT, label: 'Investment' },
    { to: ROUTE_PATHS.ABOUT, label: 'About' },
    { to: ROUTE_PATHS.CONTACT, label: 'Contact' },
  ];

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="fixed top-0 left-0 right-0 z-40 bg-primary/95 backdrop-blur-xl border-b border-accent/20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2">
              <span className="text-2xl">⚓</span>
              <span className="text-xl font-serif font-semibold text-accent tracking-wide">
                ANCHOR VAULT ESTATE
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors hover:text-accent ${
                      isActive ? 'text-accent' : 'text-primary-foreground'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                asChild
                className="text-primary-foreground hover:text-accent hover:bg-accent/10"
              >
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <FaWhatsapp className="h-5 w-5" />
                </a>
              </Button>
              <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
                <Link to={ROUTE_PATHS.CONTACT}>Contact</Link>
              </Button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-primary-foreground hover:text-accent transition-colors"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-primary border-t border-accent/20">
            <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `text-base font-medium transition-colors hover:text-accent ${
                      isActive ? 'text-accent' : 'text-primary-foreground'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-3 pt-4 border-t border-accent/20">
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  className="text-primary-foreground hover:text-accent hover:bg-accent/10"
                >
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <FaWhatsapp className="h-5 w-5" />
                  </a>
                </Button>
                <Button
                  asChild
                  className="bg-accent text-accent-foreground hover:bg-accent/90 flex-1"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Link to={ROUTE_PATHS.CONTACT}>Contact</Link>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1 pt-20">{children}</main>

      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ backgroundColor: '#25D366' }}
        className="fixed left-4 bottom-24 z-50 text-white rounded-full p-4 shadow-lg transition-all hover:scale-110"
      >
        <FaWhatsapp className="h-6 w-6" />
      </a>

      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">⚓</span>
                <span className="text-xl font-serif font-semibold text-accent">
                  ANCHOR VAULT ESTATE
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Anchored in Trust. Built for Legacy.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-accent">Quick Links</h3>
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="text-sm text-muted-foreground hover:text-accent transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-accent">Contact Info</h3>
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <a
                  href={`tel:${CONTACT_PHONE}`}
                  className="flex items-center gap-2 hover:text-accent transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  {CONTACT_PHONE}
                </a>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="hover:text-accent transition-colors"
                >
                  {CONTACT_EMAIL}
                </a>
                <p>Paje, Zanzibar, Tanzania</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-accent/20 gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 Anchor Vault Estate. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-accent transition-colors"
              >
                <FaInstagram className="h-5 w-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-accent transition-colors"
              >
                <FaFacebook className="h-5 w-5" />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-accent transition-colors"
              >
                <FaYoutube className="h-5 w-5" />
              </a>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-accent transition-colors"
              >
                <FaWhatsapp className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}